import React, { useState, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { useTheme } from '@mui/material/styles';
import { SearchIconThin } from '@buoyhealth/common.buoy-icons';

import { ErrorText } from '@buoyhealth/common.buoy-input';
import { IConditionMatch } from '@buoyhealth/common.buoy-types';
import Autocomplete, { IOption } from './Autocomplete';
import { searchConditions } from '../requests/public_site/conditions';
import Options from 'src/interviews/components/Options';
import useTransition from '../hooks/useTransition';
import useInterview from '../hooks/useInterview';
import { trackDxSelected } from './TreatmentInterview/analytics';
import type {
  IPolarisInterviewQuestion,
  ITreatmentInterviewProps,
} from 'src/packages/polaris/types/interviews';
import usePolarisActor from 'src/hooks/usePolarisActor';
import useIsPolaris from '../hooks/useIsPolaris';
import { QuestionType } from 'src/packages/polaris/types/interviews';

interface ICausesSelectionProps {
  commonCauses?: ITreatmentInterviewProps['commonCauses'];
  onQuestionSubmit?: (questionIndex: number) => void;
  question?: IPolarisInterviewQuestion;
}

const dontFilterOptions = (options: any) => options;

/** These conditions are hidden from both drop downs because they generate non-medically sound info */
const HIDDEN_CONDITIONS = ['carotid artery dissection', 'dislocated elbow'];

const CauseSelection = ({ commonCauses, question, onQuestionSubmit }: ICausesSelectionProps) => {
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState<IOption[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const state = usePolarisActor()?.getSnapshot();
  const transition = useTransition();
  const interview = useInterview();
  const questionOptions = question?.options || [];
  const showCauseOptions =
    question && questionOptions.find((o: any) => o.type === QuestionType.Single);
  const isPolaris = useIsPolaris();

  const onFreeSearchTextChange = async (query: string) => {
    if (!query) {
      return setSearchResults([]);
    }
    setSearchLoading(true);
    setSearchError('');
    try {
      const { conditions } = await searchConditions({ query });
      if (!conditions.length) {
        setSearchError('No results found. Please try modifying your query.');
      }

      const uniqueConditions: IConditionMatch[] = [];
      conditions.forEach((c) => {
        if (!uniqueConditions.find((existingCondition) => c.name === existingCondition.name)) {
          uniqueConditions.push(c);
        }
      });

      setSearchResults(
        uniqueConditions
          .map((c) => ({ value: c.slug, label: c.name }))
          .filter((c) => !HIDDEN_CONDITIONS.includes(c.label.toLowerCase())),
      );
    } catch {
      setSearchError('Sorry, something went wrong.');
    } finally {
      setSearchLoading(false);
    }
  };

  const onSelection = (val: IOption) => {
    trackDxSelected(
      val.label,
      !!commonCauses?.map((c) => c.slug).includes(val.value),
      interview.token || '',
      state?.context.interviewType,
    );
    transition({
      data: {
        isFromSearch: true,
        diagnosis: {
          name: val.label,
          slug: val.value,
        },
        question,
        isPolaris,
      },
    });
  };
  const filteredCommonCauses = useMemo(
    () =>
      commonCauses
        ?.filter((c) => !!c.slug)
        .filter((c) => !HIDDEN_CONDITIONS.includes(c.name.toLowerCase()))
        .map((c) => ({
          value: c.slug,
          label: c.name,
        })) || [],
    [commonCauses],
  );

  return (
    <>
      {!!filteredCommonCauses?.length && showCauseOptions && (
        <div className="bp-pb-3">
          <Options
            onQuestionSubmit={onQuestionSubmit}
            question={question}
            isMultiSelect={false}
            showSubmitButton={false}
            hideTopDivider={true}
          />
        </div>
      )}
      {
        <Autocomplete
          clearOnBlur={false}
          filterOptions={dontFilterOptions}
          handleChange={onSelection}
          handleInputChange={debounce(onFreeSearchTextChange, 300)}
          icon={<SearchIconThin size={24} />}
          label="Or, search for a different cause"
          loading={searchLoading}
          options={searchResults}
        />
      }

      <ErrorText message={searchError} show={!!searchError} />
    </>
  );
};

export default CauseSelection;
